import React, { useState } from 'react';
import { useGame } from '../state/gameStore.tsx';
import type { FleetSlot } from '@galaxy/shared';
import { CorvetteIcon, FrigateIcon, DestroyerIcon, CruiserIcon } from '../assets/icons.tsx';

export const FleetView: React.FC = () => {
  const { state, saveFleetFormation } = useGame();
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(null);
  const [editingSlots, setEditingSlots] = useState<FleetSlot[]>(state?.fleet.slots || []);
  const [isSaving, setIsSaving] = useState(false);

  // Sync state if initial loaded
  React.useEffect(() => {
    if (state?.fleet.slots) {
      setEditingSlots(state.fleet.slots);
    }
  }, [state?.fleet.slots]);

  if (!state) return null;

  const { blueprints, inventory } = state;

  const getSlot = (index: number) => editingSlots.find((s) => s.slot_index === index);

  const handleSlotClick = (index: number) => {
    setSelectedSlotIndex(index);
  };

  const handleAssignShip = (designId: string, count: number) => {
    if (selectedSlotIndex === null) return;

    setEditingSlots((prev) => {
      const filtered = prev.filter((s) => s.slot_index !== selectedSlotIndex);
      if (count <= 0) return filtered;
      return [...filtered, { slot_index: selectedSlotIndex, design_id: designId, ship_count: count }];
    });
    setSelectedSlotIndex(null);
  };

  const handleClearSlot = () => {
    if (selectedSlotIndex === null) return;
    setEditingSlots((prev) => prev.filter((s) => s.slot_index !== selectedSlotIndex));
    setSelectedSlotIndex(null);
  };

  const handleSaveFormation = async () => {
    try {
      setIsSaving(true);
      await saveFleetFormation(editingSlots);
    } catch {
      // Handled in store
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate estimated combat power
  const totalPower = editingSlots.reduce((acc, slot) => {
    const bp = blueprints.find((b) => b.id === slot.design_id);
    if (!bp) return acc;
    const stats = bp.stats;
    const slotDps = stats.total_damage * slot.ship_count;
    const slotHp = (stats.total_hp + stats.total_shield) * slot.ship_count;
    return acc + Math.round(Math.sqrt(slotDps * slotHp));
  }, 0);

  const getShipIcon = (hullId: string) => {
    switch (hullId) {
      case 'corvette': return <CorvetteIcon size={24} />;
      case 'frigate': return <FrigateIcon size={24} />;
      case 'destroyer': return <DestroyerIcon size={24} />;
      case 'cruiser': return <CruiserIcon size={24} />;
      default: return null;
    }
  };

  return (
    <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Title & Fleet Power Badge */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-bright)', letterSpacing: 0.5 }}>
            BỐ TRÍ ĐỘI HÌNH 3×3
          </h2>
          <p style={{ fontSize: 12, color: 'var(--text-dim)' }}>
            Hàng trước chịu đòn và che chắn cho hàng giữa & sau
          </p>
        </div>

        <div style={{
          background: 'rgba(0, 229, 255, 0.1)',
          border: '1px solid var(--accent-cyan)',
          padding: '4px 8px',
          borderRadius: 4,
          textAlign: 'right',
          flexShrink: 0,
          minWidth: 95,
        }}>
          <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700 }}>LỰC CHIẾN HẠM ĐỘI</div>
          <div className="mono" style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent-cyan)' }}>
            ⚡ {totalPower.toLocaleString()} CP
          </div>
        </div>
      </div>

      {/* 3x3 Tactical Grid */}
      <div className="sci-card" style={{ padding: '14px' }}>
        {/* Front Row Label */}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--status-red)', fontWeight: 700, marginBottom: 4 }}>
          <span>▲ HÀNG TRƯỚC (TIÊN PHONG CHỊU ĐÒN)</span>
          <span>Ô 1 - 2 - 3</span>
        </div>
        <div className="tactical-grid" style={{ marginBottom: 10 }}>
          {[1, 2, 3].map((slotIdx) => {
            const slotData = getSlot(slotIdx);
            const bp = slotData ? blueprints.find((b) => b.id === slotData.design_id) : null;
            return (
              <div
                key={slotIdx}
                className={`tactical-cell ${slotData ? 'active' : ''}`}
                onClick={() => handleSlotClick(slotIdx)}
              >
                <span className="mono" style={{ position: 'absolute', top: 4, left: 6, fontSize: 10, color: 'var(--text-dim)' }}>
                  #{slotIdx}
                </span>
                {slotData && bp ? (
                  <>
                    {getShipIcon(bp.hull_id)}
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-bright)', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '90%' }}>
                      {bp.name}
                    </span>
                    <span className="mono" style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-cyan)' }}>
                      × {slotData.ship_count}
                    </span>
                  </>
                ) : (
                  <span style={{ fontSize: 20, color: 'var(--border-panel)' }}>+</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Middle Row Label */}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--accent-amber)', fontWeight: 700, marginBottom: 4 }}>
          <span>▲ HÀNG GIỮA (HỎA LỰC TRUNG TÂM)</span>
          <span>Ô 4 - 5 - 6</span>
        </div>
        <div className="tactical-grid" style={{ marginBottom: 10 }}>
          {[4, 5, 6].map((slotIdx) => {
            const slotData = getSlot(slotIdx);
            const bp = slotData ? blueprints.find((b) => b.id === slotData.design_id) : null;
            return (
              <div
                key={slotIdx}
                className={`tactical-cell ${slotData ? 'active' : ''}`}
                onClick={() => handleSlotClick(slotIdx)}
              >
                <span className="mono" style={{ position: 'absolute', top: 4, left: 6, fontSize: 10, color: 'var(--text-dim)' }}>
                  #{slotIdx}
                </span>
                {slotData && bp ? (
                  <>
                    {getShipIcon(bp.hull_id)}
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-bright)', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '90%' }}>
                      {bp.name}
                    </span>
                    <span className="mono" style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-cyan)' }}>
                      × {slotData.ship_count}
                    </span>
                  </>
                ) : (
                  <span style={{ fontSize: 20, color: 'var(--border-panel)' }}>+</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Back Row Label */}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--accent-cyan)', fontWeight: 700, marginBottom: 4 }}>
          <span>▲ HÀNG SAU (PHÁO TẦM XA / YỂM TRỢ)</span>
          <span>Ô 7 - 8 - 9</span>
        </div>
        <div className="tactical-grid">
          {[7, 8, 9].map((slotIdx) => {
            const slotData = getSlot(slotIdx);
            const bp = slotData ? blueprints.find((b) => b.id === slotData.design_id) : null;
            return (
              <div
                key={slotIdx}
                className={`tactical-cell ${slotData ? 'active' : ''}`}
                onClick={() => handleSlotClick(slotIdx)}
              >
                <span className="mono" style={{ position: 'absolute', top: 4, left: 6, fontSize: 10, color: 'var(--text-dim)' }}>
                  #{slotIdx}
                </span>
                {slotData && bp ? (
                  <>
                    {getShipIcon(bp.hull_id)}
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-bright)', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '90%' }}>
                      {bp.name}
                    </span>
                    <span className="mono" style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-cyan)' }}>
                      × {slotData.ship_count}
                    </span>
                  </>
                ) : (
                  <span style={{ fontSize: 20, color: 'var(--border-panel)' }}>+</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Save Button */}
      <button
        className="sci-btn sci-btn-green"
        disabled={isSaving}
        onClick={handleSaveFormation}
        style={{ padding: '12px' }}
      >
        {isSaving ? 'ĐANG LƯU...' : '💾 LƯU BỐ TRÍ ĐỘI HÌNH 3×3'}
      </button>

      {/* Slot Assignment Modal */}
      {selectedSlotIndex !== null && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 16,
        }}>
          <div className="sci-card sci-card-glow" style={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent-cyan)' }}>
                BỐ TRÍ VỊ TRÍ Ô SỐ #{selectedSlotIndex}
              </h3>
              <button
                onClick={() => setSelectedSlotIndex(null)}
                style={{ background: 'none', border: 'none', color: 'var(--text-dim)', fontSize: 18, cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <p style={{ fontSize: 12, color: 'var(--text-dim)' }}>
              Chọn mẫu chiến hạm từ kho cảng để triển khai vào vị trí này:
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 250, overflowY: 'auto' }}>
              {blueprints.map((bp) => {
                const totalOwned = inventory[bp.id] || 0;
                // Currently allocated in other slots
                const usedInOtherSlots = editingSlots
                  .filter((s) => s.slot_index !== selectedSlotIndex && s.design_id === bp.id)
                  .reduce((a, s) => a + s.ship_count, 0);

                const available = Math.max(0, totalOwned - usedInOtherSlots);

                return (
                  <div
                    key={bp.id}
                    style={{
                      background: 'var(--bg-panel-subtle)',
                      border: '1px solid var(--border-panel)',
                      borderRadius: 4,
                      padding: '8px 10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-bright)' }}>
                        {bp.name}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                        Khả dụng: <span className="mono" style={{ color: 'var(--accent-cyan)' }}>{available}</span> / {totalOwned}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        className="sci-btn"
                        disabled={available < 1}
                        onClick={() => handleAssignShip(bp.id, Math.min(available, 1))}
                        style={{ padding: '4px 8px', fontSize: 11 }}
                      >
                        Gán 1
                      </button>
                      <button
                        className="sci-btn sci-btn-amber"
                        disabled={available < 1}
                        onClick={() => handleAssignShip(bp.id, available)}
                        style={{ padding: '4px 8px', fontSize: 11 }}
                      >
                        Gán Hết ({available})
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button
                className="sci-btn sci-btn-red"
                onClick={handleClearSlot}
                style={{ flex: 1, padding: '8px' }}
              >
                GỠ BỎ KHỎI Ô NÀY
              </button>
              <button
                className="sci-btn"
                onClick={() => setSelectedSlotIndex(null)}
                style={{ flex: 1, padding: '8px' }}
              >
                ĐÓNG
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
